import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useAuth } from "./lib/auth";
import { SelfServiceCreationContext, useSelfServiceCreation } from "./features/capabilities/SelfServiceCreation";
import RedirectToAccounts from "./components/RedirectToAccounts";
import SessionUnavailable from "./components/SessionUnavailable";
import { StudioRail, StudioTopbar } from "./shell";
import Landing from "./routes/Landing";
import Dashboard from "./routes/Dashboard";
import Site from "./routes/Site";
import SiteBuilds from "./routes/SiteBuilds";
import SiteBuildHistory from "./routes/SiteBuildHistory";
import SitePlugins from "./routes/SitePlugins";
import SitePreview from "./routes/SitePreview";
import SiteEditor from "./routes/SiteEditor";
import Account from "./routes/Account";
import Claim from "./routes/Claim";
import FindMyBusiness from "./routes/start/FindMyBusiness";
import BuildFromScratch from "./routes/start/BuildFromScratch";
import StartNewSite from "./routes/start/NewSite";
import AdminHome from "./routes/admin/AdminHome";
import Prospects from "./routes/admin/Prospects";
import AdminSites from "./routes/admin/Sites";
import NewSite from "./routes/admin/NewSite";
import Audit from "./routes/admin/Audit";
import { Domains, HelpCenter, Leads, MySites, WhatsNew } from "./routes/StudioPages";

export default function App() {
  const auth = useAuth();
  const location = useLocation();
  const defaultCreation = useSelfServiceCreation();
  if (auth.status === "loading") return <div className="studio-loading">Loading ProtoPal…</div>;
  if (auth.status === "error") return <div className="studio-loading"><SessionUnavailable retry={auth.retry} /></div>;
  if (auth.status === "anon") {
    return (
      <Routes>
        <Route path="/" element={<Landing />} />
        <Route path="*" element={<RedirectToAccounts />} />
      </Routes>
    );
  }
  if (/^\/sites\/[^/]+\/editor\/?$/.test(location.pathname)) {
    return (
      <Routes>
        <Route path="/sites/:siteId/editor" element={<SiteEditor />} />
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
    );
  }

  const canCreateSites = auth.me.isAdmin || (auth.me.selfServiceSiteCreationEnabled ?? defaultCreation);
  return (
    <SelfServiceCreationContext.Provider value={canCreateSites}>
    <div className="app-shell">
      <StudioTopbar />
      <StudioRail isAdmin={auth.me.isAdmin} />
      <main className="app-main studio-main">
        <Routes>
          <Route path="/" element={<Navigate to="/dashboard" replace />} />
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/sites" element={<MySites />} />
          <Route path="/domains" element={<Domains />} />
          <Route path="/leads" element={<Leads />} />
          <Route path="/help" element={<HelpCenter />} />
          <Route path="/whats-new" element={<WhatsNew />} />
          <Route path="/start" element={canCreateSites ? <StartNewSite /> : <Navigate to="/sites" replace />} />
          <Route path="/start/business" element={canCreateSites ? <FindMyBusiness /> : <Navigate to="/sites" replace />} />
          <Route path="/start/scratch" element={canCreateSites ? <BuildFromScratch /> : <Navigate to="/sites" replace />} />
          <Route path="/sites/:siteId" element={<Site />} />
          <Route path="/sites/:siteId/builds" element={<SiteBuildHistory />} />
          <Route path="/sites/:siteId/builds/:jobId" element={<SiteBuilds />} />
          <Route path="/sites/:siteId/plugins" element={<SitePlugins />} />
          <Route path="/sites/:siteId/preview" element={<SitePreview />} />
          <Route path="/sites/:siteId/editor" element={<SiteEditor />} />
          <Route path="/account" element={<Account />} />
          <Route path="/claim" element={canCreateSites ? <Claim /> : <Navigate to="/sites" replace />} />
          {auth.me.isAdmin && (
            <>
              <Route path="/admin" element={<AdminHome />} />
              <Route path="/admin/prospects" element={<Prospects />} />
              <Route path="/admin/sites" element={<AdminSites />} />
              <Route path="/admin/sites/new" element={<NewSite />} />
              <Route path="/admin/audit" element={<Audit />} />
            </>
          )}
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </main>
    </div>
    </SelfServiceCreationContext.Provider>
  );
}
